/**
 * Device admin API (STAFF-DEVICE-LOCK / FE-3)
 * ═══════════════════════════════════════════
 *
 * Backend endpoints — all `MANAGE_STAFF`-guarded, scoped to the caller's
 * organisation (see arogyakhata-orchestrator build-plan.md §1.2, FROZEN):
 *
 *   GET    /api/organisation/mem/:member_id/devices                       — list
 *   POST   /api/organisation/mem/:member_id/devices/:device_id/approve    — { name }
 *   DELETE /api/organisation/mem/:member_id/devices/:device_id            — remove
 *   GET    /api/organisation/mem/devices/summary                          — table chip feed
 *
 * List / approve / remove all return the SAME `DeviceListResponse` envelope
 * (member_id, locked, capacity, devices[]) — the single source of truth the
 * Devices modal re-renders everything from. Never re-derive it, never
 * second-fetch after a mutation.
 *
 * Every function returns `{ success, data?, error?, code? }`. `code` carries
 * the raw server `detail` string (e.g. `DEVICE_NAME_DUPLICATE`) so callers
 * can branch on the exact contract value without parsing the human-friendly
 * `error` message.
 */

import apiClient from './apiClient';

// Known device-admin error codes → plain-language fallback. The backend
// returns these as bare strings (`{"detail": "DEVICE_NOT_FOUND"}`), not
// human sentences, so callers need a translated message as well as the
// raw code.
const FRIENDLY_DEVICE_ERRORS = {
  MEMBER_NOT_FOUND: 'This staff member could not be found.',
  DEVICE_NOT_FOUND: 'This device is no longer on the list. Refresh to see the latest.',
  DEVICE_NAME_DUPLICATE: 'A device with this name already exists for this member.',
};

// Contract error `detail`s are SCREAMING_SNAKE codes. Anything else the
// backend sends as a string is internal prose — e.g. require_permission's
// "Permission 'MANAGE_STAFF' is required. Your role 'DOCTOR' does not have
// it." — which must never reach a user (no dev notes / slugs in UI copy).
const ERROR_CODE_SHAPE = /^[A-Z0-9_]+$/;

function buildDeviceApiError(error, fallback) {
  const detail = error?.response?.data?.detail;
  const code =
    typeof detail === 'string' && ERROR_CODE_SHAPE.test(detail) ? detail : undefined;

  if (code && FRIENDLY_DEVICE_ERRORS[code]) {
    return { error: FRIENDLY_DEVICE_ERRORS[code], code };
  }
  if (code) {
    // Unknown-but-well-formed code — surface it so callers can still branch,
    // with a status-based sentence for the user below.
    return { error: statusMessage(error, fallback), code };
  }

  return { error: statusMessage(error, fallback) };
}

function statusMessage(error, fallback) {
  switch (error?.response?.status) {
    case 401: return 'Session expired. Please login again.';
    case 403: return 'You do not have permission to perform this action.';
    case 422: return 'Please check the device name and try again.';
    case 500: return 'Server error. Please try again in a moment.';
    default: return fallback;
  }
}

/**
 * List a member's device rows.
 *
 * GET /api/organisation/mem/:member_id/devices
 * @param {string} memberId
 * @returns {Promise<{success, data?: object, error?: string, code?: string}>}
 */
export const fetchMemberDevices = async (memberId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/mem/${encodeURIComponent(memberId)}/devices`
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[DeviceAPI] list error:', error);
    return { success: false, ...buildDeviceApiError(error, 'Failed to load devices.') };
  }
};

/**
 * Approve a device (also used to unblock a denied one — same endpoint,
 * idempotent). Returns the full post-write envelope.
 *
 * POST /api/organisation/mem/:member_id/devices/:device_id/approve { name }
 * @param {string} memberId
 * @param {string} deviceId
 * @param {string} name trimmed, 1..40 chars
 * @returns {Promise<{success, data?: object, error?: string, code?: string}>}
 */
export const approveDevice = async (memberId, deviceId, name) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/mem/${encodeURIComponent(memberId)}/devices/${encodeURIComponent(deviceId)}/approve`,
      { name }
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[DeviceAPI] approve error:', error);
    return { success: false, ...buildDeviceApiError(error, 'Failed to approve device.') };
  }
};

/**
 * Remove (un-approve) a device row. Returns the full post-write envelope —
 * removing the last approved row flips `locked` to false.
 *
 * DELETE /api/organisation/mem/:member_id/devices/:device_id
 * @param {string} memberId
 * @param {string} deviceId
 * @returns {Promise<{success, data?: object, error?: string, code?: string}>}
 */
export const removeDevice = async (memberId, deviceId) => {
  try {
    const response = await apiClient.delete(
      `/api/organisation/mem/${encodeURIComponent(memberId)}/devices/${encodeURIComponent(deviceId)}`
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[DeviceAPI] remove error:', error);
    return { success: false, ...buildDeviceApiError(error, 'Failed to remove device.') };
  }
};

/**
 * Table chip feed. CONTRACT (§1.2.4, verified against the built backend at
 * integration): one row per member with a NON-EMPTY devices map — which
 * includes a member whose only rows are still `pending`
 * (`locked: false, approved_count: 0`). Members with no devices are absent
 * entirely (no fake zeros). Callers key the lock chip off `locked`, never
 * off row presence.
 *
 * GET /api/organisation/mem/devices/summary
 * @returns {Promise<{success, data?: {members: Array}, error?: string, code?: string}>}
 */
export const fetchDeviceSummary = async () => {
  try {
    const response = await apiClient.get('/api/organisation/mem/devices/summary');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[DeviceAPI] summary error:', error);
    return { success: false, ...buildDeviceApiError(error, 'Failed to load device summary.') };
  }
};

const deviceApi = { fetchMemberDevices, approveDevice, removeDevice, fetchDeviceSummary };
export default deviceApi;
